import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  History,
  SearchOutlined as SearchIcon,
} from "@mui/icons-material";
import { ReactComponent as SortBySVG } from "../../../assets/icons/sort-by.svg";
import "./search-sort.scss";

interface Suggestion {
  display: string;
  type: "planner" | "location" | "partner";
  data: any;
}

interface SearchSortProps {
  searchValue: string;
  sortValue: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSortChange: (e: SelectChangeEvent) => void;
  sortOptions?: {
    value: string;
    label: string;
  }[];
  placeholder?: string;
  suggestions?: Suggestion[];
  onSuggestionClick?: (suggestion: Suggestion) => void;
  loading?: boolean;
  onSearch?: () => void;
  recentSearches?: string[];
  onClearRecentSearches?: () => void;
  onRecentSearchClick?: (searchTerm: string) => void;
  onRemoveRecentSearch?: (searchTerm: string) => void;
}

const CustomSortIcon = () => (
  <Box
    component="span"
    sx={{
      display: "flex",
      alignItems: "center",
      pr: 1,
    }}
  >
    <SortBySVG />
  </Box>
);

const CustomMobileSortIcon = () => (
  <Box
    component="span"
    sx={{
      display: "flex",
      alignItems: "center",
      color: "#104145",
      backgroundColor: "#fff",
      padding: "0.5rem",
      px: "0.7rem",
      borderRadius: "8px",
    }}
  >
    <SortBySVG />
  </Box>
);

// const sortOptions = [
//   { value: "low_to_high", label: "Price (Low to High)" },
//   { value: "high_to_low", label: "Price (High to Low)" },
//   { value: "distance", label: "Distance" },
//   { value: "rating", label: "Rating" },
// ];

export default function SearchSort({
  searchValue,
  sortValue,
  onSearchChange,
  onSortChange,
  sortOptions,
  placeholder = "Search",
  suggestions = [],
  onSuggestionClick,
  loading = false,
  onSearch,
  recentSearches = [],
  onClearRecentSearches = () => {},
  onRecentSearchClick = () => {},
  onRemoveRecentSearch = () => {},
}: SearchSortProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const searchRef = useRef<HTMLDivElement>(null);

  // STATES
  const [isToggleVisible, setIsToggleVisible] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowSuggestions(true);
    onSearchChange(e);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setShowSuggestions(false);
    if (onSuggestionClick) {
      onSuggestionClick(suggestion);
      sessionStorage.setItem(
        "searchPlannerSuggestion",
        JSON.stringify(suggestion)
      );
    }
  };

  const handleClearSearch = () => {
    onSearchChange({
      target: { value: "" },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
    const storedSuggestion = sessionStorage.getItem("searchPlannerSuggestion");
    if (storedSuggestion) {
      sessionStorage.removeItem("searchPlannerSuggestion");
    }
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch();
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        !searchValue
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchValue]);

  // ---- MOBILE COMPONENT ----
  if (isMobile) {
    return (
      <Box
        ref={searchRef}
        className="search-sort-mobile"
        display="flex"
        flexDirection="row"
        justifyContent="center"
        gap={2}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
          "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
            {
              borderWidth: "1px",
              borderColor: "rgba(0, 0, 0, 0.23)",
            },
        }}
      >
        {/* Search */}
        <Box sx={{ position: "relative", width: "100%" }}>
          <TextField
            // variant="outlined"
            fullWidth
            size="small"
            placeholder="Search"
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {Boolean(searchValue) && (
                    <IconButton
                      aria-label="Clear search"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <Close sx={{ color: "#104145" }} fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton aria-label="Search" onClick={handleSearch}>
                    <SearchIcon sx={{ color: "#104145" }} />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                height: 36,
                borderRadius: "8px",
              },
            }}
            sx={{
              bgcolor: "#fff",
              borderRadius: "8px",
              fontSize: "16px",
              height: 36,
              boxShadow: "0px 0px 3px 1px #00000033",
            }}
            value={searchValue}
            onChange={handleSearchChange}
          />
          {/* Suggestions Dropdown Mobile */}
          {showSuggestions &&
            (searchValue ? (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  mt: 0.5,
                  maxHeight: 200,
                  overflowY: "auto",
                  borderRadius: "8px",
                }}
              >
                {loading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 1,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <List>
                    {suggestions.length > 0 ? (
                      suggestions.map((suggestion, index) => (
                        <ListItem
                          key={index}
                          disablePadding
                          onClick={() => handleSuggestionClick(suggestion)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                          }}
                        >
                          <ListItemText
                            primary={suggestion.display}
                            sx={{ px: 2, py: 0.5, my: 0 }}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <ListItem sx={{ textAlign: "center", py: 1 }}>
                        <ListItemText
                          primary="No results found"
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: { color: "text.secondary" },
                          }}
                        />
                      </ListItem>
                    )}
                  </List>
                )}
              </Paper>
            ) : (
              <Paper
                elevation={3}
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  mt: 0.5,
                  maxHeight: 200,
                  overflowY: "auto",
                  borderRadius: "8px",
                }}
              >
                <List>
                  {recentSearches.length > 0 && (
                    <ListItem
                      secondaryAction={
                        <Typography
                          onClick={onClearRecentSearches}
                          sx={{
                            cursor: "pointer",
                            color: "#104145",
                            fontSize: "12px",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          Clear All
                        </Typography>
                      }
                    >
                      <ListItemText primary="Recent Searches" />
                    </ListItem>
                  )}
                  {recentSearches.length > 0 ? (
                    recentSearches.map((searchTerm, index) => (
                      <ListItem
                        key={index}
                        onClick={() => onRecentSearchClick(searchTerm)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="remove recent search"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveRecentSearch(searchTerm);
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
                          <History fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${searchTerm}`}
                          primaryTypographyProps={{
                            variant: "body2",
                            sx: {
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="No recent searches" />
                    </ListItem>
                  )}
                </List>
              </Paper>
            ))}
        </Box>

        {/* Sort Dropdown */}
        <Box
          onClick={() => setIsToggleVisible(!isToggleVisible)}
          sx={{
            display: !isToggleVisible ? "none" : "",
            boxShadow: "0px 0px 3px 1px #00000033 !important",
            borderRadius: "10px",
            cursor: "pointer",
          }}
        >
          {isToggleVisible && <CustomMobileSortIcon />}
        </Box>
        {!isToggleVisible && (
          <FormControl
            size="small"
            fullWidth
            sx={{
              bgcolor: "#fff",
              borderRadius: "10px",
              boxShadow: "0px 0px 3px 1px #00000033",
              height: 36,
            }}
          >
            <Select
              displayEmpty
              value={sortValue}
              onChange={onSortChange}
              IconComponent={CustomSortIcon}
              sx={{
                ".MuiSelect-select": {
                  paddingRight: "32px",
                  fontSize: "14px",
                  height: 20,
                },
                ".MuiOutlinedInput": {
                  borderRadius: "10px !important",
                },
              }}
            >
              <MenuItem value="" disabled>
                Sort By
              </MenuItem>
              {sortOptions?.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{
                    color: "#104145",
                    "&.Mui-selected": {
                      bgcolor: "#15585D33 !important",
                      color: "#104145",
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {/* Show Chip if sortValue exists */}
          </FormControl>
        )}
      </Box>
    );
  }

  // ---- DESKTOP COMPONENT ----
  return (
    <Box
      ref={searchRef}
      className="search-sort"
      sx={{
        position: "relative", // For absolute positioning of suggestions
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
          {
            borderWidth: "1px",
            borderColor: "rgba(0, 0, 0, 0.23)",
          },
      }}
    >
      {/* Search */}
      <Box className="searchbox">
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {Boolean(searchValue) && (
                  <IconButton
                    aria-label="Clear search"
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <Close sx={{ color: "#104145" }} fontSize="small" />
                  </IconButton>
                )}
                <IconButton aria-label="Search" onClick={handleSearch}>
                  <SearchIcon sx={{ color: "#104145" }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "#fff",
            borderRadius: "8px",
            fontSize: "18px",
          }}
          value={searchValue}
          onChange={handleSearchChange}
        />
        {/* Suggestions Dropdown */}
        {showSuggestions &&
          (searchValue ? (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                mt: 0.5,
                maxHeight: 200,
                overflowY: "auto",
                borderRadius: "8px",
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List>
                  {suggestions.length > 0 ? (
                    suggestions.map((suggestion, index) => (
                      <ListItem
                        key={index}
                        disablePadding
                        onClick={() => handleSuggestionClick(suggestion)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                        }}
                      >
                        <ListItemText
                          primary={suggestion.display}
                          sx={{ px: 2, py: 0.5, my: 0 }}
                          primaryTypographyProps={{ variant: "body2" }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem sx={{ py: 1, textAlign: "center" }}>
                      <ListItemText
                        primary="No results found"
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: { color: "text.secondary" },
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          ) : (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                mt: 0.5,
                maxHeight: 200,
                overflowY: "auto",
                borderRadius: "8px",
              }}
            >
              <List>
                {recentSearches.length > 0 && (
                  <ListItem
                    secondaryAction={
                      <Typography
                        onClick={onClearRecentSearches}
                        sx={{
                          cursor: "pointer",
                          color: "#104145",
                          fontSize: "12px",
                          fontWeight: 600,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Clear All
                      </Typography>
                    }
                  >
                    <ListItemText primary="Recent Searches" />
                  </ListItem>
                )}
                {recentSearches.length > 0 ? (
                  recentSearches.map((searchTerm, index) => (
                    <ListItem
                      key={index}
                      onClick={() => onRecentSearchClick(searchTerm)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="remove recent search"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveRecentSearch(searchTerm);
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
                        <History fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${searchTerm}`}
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: {
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                    </ListItem>
                  ))
                ) : (
                    <ListItem sx={{ margin: 0, padding: "0 8px" }}>
                      <ListItemText primary="No recent searches" sx={{ padding: 0, margin: 0 }} />
                    </ListItem>
                )}
              </List>
            </Paper>
          ))}
      </Box>

      {/* Sort By */}
      <Box className="sortby" display="flex" alignItems="center">
        <Typography sx={{ color: "#9C9991", whiteSpace: "nowrap" }}>
          Sort By
        </Typography>
        <FormControl
          size="small"
          sx={{
            marginLeft: 1,
            bgcolor: "#fff",
            borderRadius: "10px",
          }}
        >
          <Select
            displayEmpty
            value={sortValue}
            onChange={onSortChange}
            className="sort-by-select"
            IconComponent={CustomSortIcon}
            sx={{
              ".MuiSelect-select": {
                paddingRight: "32px",
                fontSize: "14px",
              },
            }}
          >
            <MenuItem value="" disabled>
              Select
            </MenuItem>
            {sortOptions?.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{
                  color: "#104145",
                  "&.Mui-selected": {
                    bgcolor: "#15585D33 !important",
                    color: "#104145",
                  },
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
